import { LucideIcon } from 'lucide-react';
import type { AssetType } from '../types/chart.types';

interface KPICardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  unit?: string;
  color?: 'blue' | 'green' | 'amber' | 'red' | 'gray';
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
  size?: 'sm' | 'md' | 'lg';
  alert?: boolean;
  subtitle?: string;
  onClick?: () => void;
  kpiName?: string;
  assetType?: AssetType;
  clickable?: boolean;
}

const colorConfig = {
  blue: { icon: 'text-ems-blue', bg: 'bg-ems-blue/10', border: 'border-ems-blue/20' },
  green: { icon: 'text-ems-green', bg: 'bg-ems-green/10', border: 'border-ems-green/20' },
  amber: { icon: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  red: { icon: 'text-ems-red', bg: 'bg-ems-red/10', border: 'border-ems-red/20' },
  gray: { icon: 'text-ems-text-muted', bg: 'bg-ems-bg', border: 'border-ems-border' },
};

export function KPICard({
  icon: Icon,
  label,
  value,
  unit,
  color = 'blue',
  trend,
  trendValue,
  size = 'md',
  alert = false,
  subtitle,
  onClick,
  clickable = false,
}: KPICardProps) {
  const c = colorConfig[color];

  const sizeClasses = { sm: 'p-3', md: 'p-4', lg: 'p-5' };
  const valueSizes = { sm: 'text-lg', md: 'text-2xl', lg: 'text-3xl' };

  return (
    <div
      onClick={clickable && onClick ? onClick : undefined}
      className={`${sizeClasses[size]} rounded-lg border ${c.bg} ${c.border} ${
        alert ? 'border-2 animate-pulse' : ''
      } ${clickable ? 'cursor-pointer hover:bg-ems-bg-card-hover' : ''} transition-all flex flex-col`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-4 h-4 ${c.icon}`} />
          <span className="text-xs text-ems-text-muted font-medium">{label}</span>
        </div>
        {trend && (
          <span className={`text-xs ${trend === 'up' ? 'text-ems-green' : trend === 'down' ? 'text-ems-red' : 'text-ems-text-muted'}`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
            {trendValue !== undefined && ` ${trendValue.toFixed(1)}%`}
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-1.5 flex-grow">
        <span className={`${valueSizes[size]} font-bold text-ems-text-primary font-mono`}>
          {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
        </span>
        {unit && <span className="text-xs text-ems-text-muted">{unit}</span>}
      </div>

      {subtitle && <p className="mt-1.5 text-xs text-ems-text-muted">{subtitle}</p>}
    </div>
  );
}
